import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ApiClient from "../client";
import { ENDPOINTS } from "../endpoints";
import { CreateCompanyDto, UpdateCompanyDto } from "../types";
import { queryKeys } from "../keys";

const client = new ApiClient(process.env.API_URL!, "app");

// Queries
export const useCompanies = () =>
    useQuery({
        queryKey: queryKeys.companies,
        queryFn: () => client.get(ENDPOINTS.COMPANIES),
    });

export const useClientCompanies = (companyIds: string[]) =>
    useQuery({
        queryKey: queryKeys.clientCompanies(companyIds),
        queryFn: () =>
            Promise.all(
                companyIds
                    .filter((id) => Boolean(id))
                    .map((id) => client.get(ENDPOINTS.COMPANY(id)))
            ),
        enabled: !!companyIds.length,
    });

// Mutations
export const useCreateCompany = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: CreateCompanyDto) =>
            client.post(ENDPOINTS.COMPANIES, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.companies });
        },
    });
};

export const useUpdateCompany = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: UpdateCompanyDto }) =>
            client.patch(ENDPOINTS.COMPANY(id), data),
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.companies });
            queryClient.invalidateQueries({ queryKey: queryKeys.company(id) });
        },
    });
};

export const useDeleteCompany = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => client.delete(ENDPOINTS.COMPANY(id)),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.companies });
        },
    });
};
